import { ORDO_NODES, RESPONSIBILITY_IDS } from "@/content/ordo";
import { PageIntro } from "@/components/site/PageIntro";
import { Placeholder } from "@/components/site/Placeholder";
import { EngageBand } from "@/components/site/EngageBand";
import { Container, Eyebrow, Mono, SectionTitle, StatusMarker, TextLink } from "@/components/site/primitives";

const COMPARE = [
  ["What it is", "The governing architecture, incorporated into another organization's intelligent system.", "Four applied capabilities, each with its own responsibility."],
  ["Who it concerns", "Organizations developing intelligent systems.", "Organizations adopting software for their own situation."],
  ["How it is reached", "Technical collaboration and licensing.", "Through Ordo Diagnostic, or directly where appropriate."],
];

const FORMS = [
  { title: "Scoped architectural examination", text: "A deeper look at the architecture than this public site provides, under an appropriate arrangement." },
  { title: "Technical collaboration", text: "Working together on how the architecture would be incorporated into a particular system." },
  { title: "Licensing discussion", text: "Exploring the terms under which General Ordo could be incorporated as infrastructure." },
];

const Distinction = () => (
  <section className="border-b border-o-line" data-testid="infra-distinction">
    <Container className="py-16 lg:py-24">
      <Eyebrow index="01">A licensing route, not a fifth product</Eyebrow>
      <SectionTitle className="mt-5 max-w-3xl text-3xl sm:text-4xl lg:text-4xl">Infrastructure and applied software are different relationships.</SectionTitle>
      <div className="mt-10 overflow-x-auto">
        <table className="w-full min-w-[640px] border border-o-line text-left">
          <thead>
            <tr className="bg-o-surface">
              <th className="w-48 p-5"><Mono>Compared</Mono></th>
              <th className="p-5 font-display text-lg font-bold">General Ordo Infrastructure</th>
              <th className="p-5 font-display text-lg font-bold">
                Applied software <TextLink to="/enterprise/software" testid="infra-software-link" className="ml-2 align-middle">View</TextLink>
              </th>
            </tr>
          </thead>
          <tbody>
            {COMPARE.map(([k, a, b]) => (
              <tr key={k} className="border-t border-o-line">
                <th scope="row" className="p-5 align-top font-normal"><Mono>{k}</Mono></th>
                <td className="p-5 align-top">{a}</td>
                <td className="p-5 align-top text-o-dim">{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  </section>
);

const Incorporation = () => (
  <section className="border-b border-o-line" data-testid="infra-incorporation">
    <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-4">
        <Eyebrow index="02">What incorporation concerns</Eyebrow>
        <p className="mt-5 leading-relaxed text-o-dim">An intelligent system incorporating General Ordo would account for each governing responsibility. Mechanisms are not described here.</p>
        <TextLink to="/ordo#explorer" testid="infra-explorer-link" className="mt-6">Open the architecture explorer</TextLink>
      </div>
      <ol className="border-t border-o-line lg:col-span-8">
        {RESPONSIBILITY_IDS.map((id) => (
          <li key={id} className="grid gap-2 border-b border-o-line py-5 sm:grid-cols-12 sm:gap-6">
            <Mono accent className="sm:col-span-1">{ORDO_NODES[id].index}</Mono>
            <span className="font-display text-xl font-bold sm:col-span-4">{ORDO_NODES[id].label}</span>
            <span className="text-o-dim sm:col-span-7">{ORDO_NODES[id].concerns}</span>
          </li>
        ))}
      </ol>
    </Container>
  </section>
);

const Forms = () => (
  <section className="border-b border-o-line" data-testid="infra-forms">
    <Container className="py-16 lg:py-24">
      <Eyebrow index="03">Proposed forms of technical engagement</Eyebrow>
      <div className="mt-8 grid gap-3 md:grid-cols-3">
        {FORMS.map((f, i) => (
          <div key={f.title} className="flex flex-col gap-6 border border-o-line bg-o-surface p-6">
            <Mono accent>0{i + 1}</Mono>
            <div>
              <h3 className="font-display text-xl font-bold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-o-dim">{f.text}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4"><span className="draft-tag">Proposed · to be confirmed</span></p>
    </Container>
  </section>
);

export default function InfrastructurePage() {
  return (
    <>
      <PageIntro routeKey="infrastructure" group="ordo" index="GI" eyebrow="Separate licensing route" title="General Ordo Infrastructure"
        lede="How the governing architecture could be incorporated into intelligent systems developed by other organizations."
        aside={<Placeholder label="Infrastructure incorporation diagram" note="General Ordo within another organization's system" ratio="4 / 3" testid="placeholder-infrastructure" />}>
        <div className="mt-8"><StatusMarker testid="status-infrastructure" /></div>
      </PageIntro>
      <Distinction />
      <Incorporation />
      <Forms />
      <EngageBand path="technical" from="/ordo/infrastructure" title="Discuss incorporating General Ordo."
        text="For organizations building intelligent systems. This opens the technical and infrastructure collaboration inquiry, an inactive preview during Phase 1." />
    </>
  );
}
